import { useCallback, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export function validateEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function validatePassword(password) {
  return password.length >= 6;
}

export default function useAuthActions() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const clearMessages = useCallback(() => {
    setError(null);
    setSuccess(null);
  }, []);

  const handleLogin = useCallback(
    async ({ username, password }) => {
      setError(null);
      setSuccess(null);
      setLoading(true);

      try {
        const loginData = new URLSearchParams();
        loginData.append('username', username);
        loginData.append('password', password);

        const response = await axios.post(`${API_BASE_URL}/auth/login`, loginData, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        });

        const { access_token, user_type, user_id } = response.data;

        if (!access_token) {
          throw new Error('No access token received');
        }

        try {
          jwtDecode(access_token);
        } catch {
          throw new Error('Invalid token format');
        }

        if (!user_type || !user_id) {
          throw new Error('Missing user details in token');
        }

        if (user_type === 'patient') {
          setError('Patients can only access via mobile app.');
          setLoading(false);
          return;
        }

        login(access_token, user_type, user_id);
        setSuccess('Signed in successfully.');

        setTimeout(() => {
          if (user_type === 'admin') {
            navigate('/administrator');
          } else {
            navigate('/mydashboard');
          }
        }, 400);
      } catch (err) {
        const detail = err.response?.data?.detail;
        if (detail && (/user not active/i.test(detail) || /contact admin/i.test(detail))) {
          setError('Contact Admin, user not active.');
        } else {
          setError(detail || err.message || 'An error occurred. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    },
    [login, navigate],
  );

  const handleSignup = useCallback(
    async (formData, onSuccess) => {
      setError(null);
      setSuccess(null);
      setLoading(true);

      try {
        if (!validateEmail(formData.email)) {
          throw new Error('Please enter a valid email address');
        }
        if (!validatePassword(formData.password)) {
          throw new Error('Password must be at least 6 characters');
        }

        const response = await fetch(`${API_BASE_URL}/auth/signup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: formData.username,
            password: formData.password,
            email: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName,
            inviteKey: formData.inviteKey,
          }),
        });

        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.detail || 'Signup failed');
        }

        setSuccess('Account created. You can sign in now.');
        onSuccess?.(formData.username);
      } catch (err) {
        setError(err.message || 'An error occurred. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    loading,
    error,
    success,
    clearMessages,
    handleLogin,
    handleSignup,
    setError,
    setSuccess,
  };
}
